import React, { useMemo, useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Divider,
  Box,
  Chip,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  ButtonGroup,
  Tooltip,
  IconButton,
  TextField,
  Button,
  Autocomplete,
  TableFooter
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DownloadIcon from '@mui/icons-material/Download';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'; // ✅ YENİ: Saat seçimi için
import { tr } from 'date-fns/locale';
import { format, parseISO, isValid } from 'date-fns';
import { utils, writeFile } from 'xlsx';
import accountingService from '../../../../services/accountingService'; // ✅ YENİ: SQL API
import { useTranslation } from 'react-i18next';

// SalesScreenReportsPage.tsx'den PaymentOption ve BankAccount/SubAccount arayüzlerini alalım
export type PaymentOptionType = 'cash' | 'pos' | 'openAccount' | 'voucher' | 'receipt' | 'bonus' | 'accrual';

export interface PaymentOption {
  id: string;
  type: PaymentOptionType;
  label: string;
  enabled: boolean;
  cashboxId?: string | null;
  bankAccountId?: string | null;
  defaultAccountId?: string | null;
  timestamp?: number;
  subAccountId?: string | null;
  relatedAccount?: string;
}

interface SubAccountForModal {
  id: string;
  accountName: string;
  accountNumber: string;
  isPosAccount?: boolean;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  isPosAccount?: boolean;
  subAccounts?: { [key: string]: SubAccountForModal };
}

// BankTransactionReport.tsx'tan BankTransaction arayüzü
interface BankTransaction {
  id: string;
  date: string; // ISO formatında olmalı
  amount: number;
  bankAccountId: string;
  bankAccountName: string; // Genellikle ana banka adı + hesap no olur
  bankName: string; // Sadece banka adı (örn: Ziraat Bankası)
  bankSubAccountId?: string;
  description: string;
  transactionType: string; // deposit, withdrawal, QUICK_SALE vb.
  type?: 'INCOME' | 'EXPENSE'; // Gelir/Gider (manuel eklenenlerde)
  currency: string;
  relatedAccountId?: string;
  relatedAccountName?: string;
  documentNo?: string;
  createdAt: string; // Tam zaman damgası (ISO formatında olmalı)
  updatedAt?: string;
  createdBy?: string;
  paymentMethod?: string;
  referenceId?: string; // Hızlı satış ID'si gibi
  referenceType?: string; // 'QUICK_SALE' gibi
  screenId?: string;
}

interface PosReportTabProps {
  paymentOptions: PaymentOption[];
  allBankAccounts: BankAccount[];
  companyId: string | null;
  screenId: string | null;
}

// Tarih ve Saati Formatlama Fonksiyonu
// ⚠️ Backend'den gelen tarih ZATEN UTC+3 formatında, tekrar çevirmeye gerek yok!
const formatDateTime = (isoDateString: string): string => {
  if (!isoDateString) return '-';
  try {
    // Backend'den gelen string: "2025-11-27T09:18:27.3824304"
    // Bu ZATEN Türkiye saati (UTC+3), sadece formatla
    const date = new Date(isoDateString);
    if (isValid(date)) {
      // date-fns format fonksiyonu local time olarak gösterir
      return format(date, 'dd.MM.yyyy HH:mm', { locale: tr });
    }
    return '-';
  } catch (error) {
    return '-';
  }
};

// Para birimi formatla (SalesScreenReportsPage.tsx'tan alındı)
const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2
  }).format(amount);
};

// Firebase tarihini ISO string'e çevir (SalesScreenReportsPage.tsx'deki mantığa benzer)
const convertToISODateIfNeeded = (firebaseDate: any): string => {
  if (!firebaseDate) return '';
  if (typeof firebaseDate === 'string') {
    // Zaten ISO formatında veya parse edilebilir bir string ise direkt kullan
    try {
      const parsed = parseISO(firebaseDate);
      if (isValid(parsed)) return firebaseDate;
    } catch (e) { /* ignore */ }
  }
  // Firebase Timestamp (seconds ve nanoseconds var) veya sayı ise Date objesine çevir
  if ((typeof firebaseDate === 'object' && firebaseDate.seconds) || typeof firebaseDate === 'number') {
    const date = typeof firebaseDate === 'number' ? new Date(firebaseDate) : new Date(firebaseDate.seconds * 1000);
    if (isValid(date)) return date.toISOString();
  }
  // Eğer dd.MM.yyyy formatında ise parse etmeye çalış
  if (typeof firebaseDate === 'string' && firebaseDate.includes('.')) {
    try {
      const parts = firebaseDate.split('.');
      if (parts.length === 3) {
        const date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        if (isValid(date)) return date.toISOString();
      }
    } catch (e) { /* ignore */ }
  }
  console.warn("Geçersiz tarih formatı, dönüştürülemedi:", firebaseDate);
  return ''; 
};

// İşlem tipi için ikon ve etiket (BankTransactionReport.tsx'tan uyarlandı)
const getPosTransactionTypeInfo = (transaction: BankTransaction, t: (key: string) => string) => {
  const transType = transaction.transactionType?.toUpperCase();
  const type = transaction.type; // INCOME veya EXPENSE

  if (transType === 'QUICK_SALE') {
    return { label: t('posReportTab.transactionTypes.quickSale'), icon: <PointOfSaleIcon fontSize="small" color="info" />, color: 'info' as 'info' };
  }
  if (transType === 'DEPOSIT' || type === 'INCOME') {
    return { label: t('posReportTab.transactionTypes.cashIn'), icon: <AttachMoneyIcon fontSize="small" color="success" />, color: 'success' as 'success' };
  }
  if (transType === 'WITHDRAWAL' || type === 'EXPENSE') {
    return { label: t('posReportTab.transactionTypes.cashOut'), icon: <MoneyOffIcon fontSize="small" color="error" />, color: 'error' as 'error' };
  }
  return { label: transaction.transactionType || t('posReportTab.common.other'), icon: <ReceiptIcon fontSize="small" />, color: 'default' as 'default' };
};

const PosReportTab: React.FC<PosReportTabProps> = ({ paymentOptions, allBankAccounts, companyId, screenId }) => {
  const { t } = useTranslation('salesSystem');

  const [posTransactions, setPosTransactions] = useState<BankTransaction[]>([]);
  const [filteredPosTransactions, setFilteredPosTransactions] = useState<BankTransaction[]>([]);
  const [loadingPosTransactions, setLoadingPosTransactions] = useState<boolean>(false);
  const [posTransactionsError, setPosTransactionsError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedCashier, setSelectedCashier] = useState<string | null>(null);

  const assignedPosInfos = useMemo(() => {
    if (!paymentOptions || !allBankAccounts) return [];
    const activePosOptions = paymentOptions.filter(
      (option) => option.type === 'pos' && option.enabled && option.bankAccountId
    );
    return activePosOptions.map(option => {
      const bank = allBankAccounts.find(b => b.id === option.bankAccountId);
      if (!bank) return { id: option.id, name: option.label, bankDetails: t('posReportTab.common.bankNotFound'), bankAccountId: option.bankAccountId, subAccountId: option.subAccountId };

      let accountName = bank.bankName;
      if (option.subAccountId && bank.subAccounts && bank.subAccounts[option.subAccountId]) {
        accountName = `${bank.bankName} - ${bank.subAccounts[option.subAccountId].accountName}`;
      }
      return {
        id: option.id,
        name: option.label, 
        bankDetails: accountName,
        bankAccountId: option.bankAccountId,
        subAccountId: option.subAccountId
      };
    });
  }, [paymentOptions, allBankAccounts]);

  useEffect(() => {
    const loadPosTransactions = async () => {
      if (!companyId || assignedPosInfos.length === 0 || !screenId) {
        setPosTransactions([]);
        setFilteredPosTransactions([]);
        setSelectedCashier(null);
        return;
      }
      setLoadingPosTransactions(true);
      setPosTransactionsError(null);
      
      console.log('📋 [POS RAPORU] SQL\'den banka hareketleri yükleniyor...', {
        companyId,
        screenId,
        posCount: assignedPosInfos.length
      });
      
      try {
        // ✅ SQL'den banka hareketlerini çek
        const sqlTransactions = await accountingService.getBankTransactionsByCompany();
        
        console.log(`✅ [POS RAPORU] SQL'den ${sqlTransactions.length} banka hareketi yüklendi`);
        console.log('📊 [POS RAPORU] İlk hareket örneği:', sqlTransactions[0]);
        
        // SQL verilerini frontend formatına çevir ve filtrele
        // ⚠️ Backend'den gelen veri camelCase formatında (id, bankAccountId, screenId)
        const allScreenPosTransactions: BankTransaction[] = sqlTransactions
          .filter((trans: any) => {
            // 1. POS hesaplarından biri olmalı
            const matchesPosAccount = assignedPosInfos.some(posInfo =>
              posInfo.bankAccountId === trans.bankAccountId?.toString() &&
              (posInfo.subAccountId ? posInfo.subAccountId === trans.bankSubAccountId?.toString() : true)
            );
            
            // 2. Bu ekrana ait olmalı
            const matchesScreen = trans.screenId?.toString() === screenId?.toString();
            
            console.log(`🔍 [POS RAPORU] İşlem ${trans.id}: bankAccountId=${trans.bankAccountId}, screenId=${trans.screenId}, matchesPosAccount=${matchesPosAccount}, matchesScreen=${matchesScreen}`);
            
            return matchesPosAccount && matchesScreen;
          })
          .map((trans: any) => ({
            id: trans.id?.toString() || '',
            date: trans.date || trans.createdAt || '',
            amount: parseFloat(trans.amount) || 0,
            bankAccountId: trans.bankAccountId?.toString() || '',
            bankAccountName: trans.bankAccount?.bankName || t('posReportTab.common.unknownBank'),
            bankName: trans.bankAccount?.bankName || t('posReportTab.common.unknownBank'),
            bankSubAccountId: trans.bankSubAccountId?.toString(),
            description: trans.description || '',
            transactionType: trans.transactionType || 'POS_SALE',
            type: trans.type || 'INCOME',
            currency: trans.currency || 'TRY',
            relatedAccountId: trans.relatedAccountId?.toString(),
            relatedAccountName: trans.relatedAccountName,
            documentNo: trans.documentNo,
            createdAt: trans.createdAt || '',
            updatedAt: trans.updatedAt,
            createdBy: trans.createdByEmail || '',
            paymentMethod: trans.paymentMethod || 'card_payment',
            referenceId: trans.referenceId?.toString(),
            referenceType: trans.referenceType,
            screenId: trans.screenId?.toString()
          }));
        
        // Tarihe göre azalan sıralama (en yeni en üstte)
        allScreenPosTransactions.sort((a, b) => {
          const dateA = a.createdAt || a.date;
          const dateB = b.createdAt || b.date;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
        
        console.log(`✅ [POS RAPORU] ${allScreenPosTransactions.length} hareket (ekran ID: ${screenId}) işlendi ve sıralandı`);
        
        setPosTransactions(allScreenPosTransactions);
        setFilteredPosTransactions(allScreenPosTransactions);
      } catch (error) {
        console.error("POS hareketleri yüklenirken hata:", error);
        setPosTransactionsError(t('posReportTab.errors.loadFailed'));
      } finally {
        setLoadingPosTransactions(false);
      }
    };

    loadPosTransactions();
  }, [companyId, assignedPosInfos, screenId]);

  useEffect(() => {
    let filtered = [...posTransactions];

    // Kasiyer Filtresi
    if (selectedCashier) {
      filtered = filtered.filter(transaction => transaction.createdBy === selectedCashier);
    }

    if (dateFilter) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let filterStartDate: Date;
      switch (dateFilter) {
        case 'today':
          filterStartDate = today;
          const endOfToday = new Date(today);
          endOfToday.setHours(23, 59, 59, 999);
          filtered = filtered.filter(t => {
            const transactionDate = parseISO(t.createdAt || t.date);
            return transactionDate >= filterStartDate && transactionDate <= endOfToday;
          });
          break;
        case 'week':
          filterStartDate = new Date(today);
          filterStartDate.setDate(today.getDate() - 7);
          filtered = filtered.filter(t => parseISO(t.createdAt || t.date) >= filterStartDate);
          break;
        case 'month':
          filterStartDate = new Date(today);
          filterStartDate.setMonth(today.getMonth() - 1);
          filtered = filtered.filter(t => parseISO(t.createdAt || t.date) >= filterStartDate);
          break;
      }
    } else if (startDate || endDate) {
      // ✅ DateTimePicker kullanıldığı için saat bilgisi korunuyor
      const sDate = startDate ? new Date(startDate) : null;
      const eDate = endDate ? new Date(endDate) : null;
      filtered = filtered.filter(t => {
        const transactionDate = parseISO(t.createdAt || t.date);
        const afterStartDate = sDate ? transactionDate >= sDate : true;
        const beforeEndDate = eDate ? transactionDate <= eDate : true;
        return afterStartDate && beforeEndDate;
      });
    }
    setFilteredPosTransactions(filtered);
    setPage(0);
  }, [posTransactions, dateFilter, startDate, endDate, selectedCashier]);

  const totalFilteredPosAmount = useMemo(() => {
    return filteredPosTransactions.reduce((sum, transaction) => {
      // QUICK_SALE işlemleri genellikle gelir (pozitif) kabul edilir.
      // Diğer işlemler için 'type' (INCOME/EXPENSE) kontrol edilebilir veya miktarın işaretine bakılabilir.
      if (transaction.transactionType === 'QUICK_SALE') {
        return sum + Math.abs(transaction.amount); // Her zaman pozitif ekle
      } else if (transaction.type === 'INCOME') {
        return sum + transaction.amount;
      } else if (transaction.type === 'EXPENSE') {
        return sum - transaction.amount;
      } else {
        // type belirtilmemişse amount işaretine göre işlem yap
        return sum + transaction.amount; 
      }
    }, 0);
  }, [filteredPosTransactions]);

  // Tablo için genel POS tutar toplamı
  const overallPosTotalAmount = useMemo(() => {
    return filteredPosTransactions.reduce((sum, transaction) => {
        // transactionType QUICK_SALE ise pozitif, INCOME ise pozitif, EXPENSE ise negatif olarak toplama dahil et
        // type belirtilmemişse amount işaretine göre işlem yap
        if (transaction.transactionType === 'QUICK_SALE') {
            return sum + Math.abs(transaction.amount); 
        } else if (transaction.type === 'INCOME') {
            return sum + transaction.amount;
        } else if (transaction.type === 'EXPENSE') {
            return sum - transaction.amount;
        } else {
            return sum + transaction.amount; 
        }
    }, 0);
  }, [filteredPosTransactions]);

  const paginatedPosTransactions = useMemo(() => {
    return filteredPosTransactions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredPosTransactions, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDateFilterLabel = (): string => {
    if (dateFilter) {
      switch (dateFilter) {
        case 'today': return t('posReportTab.dateFilters.today');
        case 'week': return t('posReportTab.dateFilters.lastWeek');
        case 'month': return t('posReportTab.dateFilters.lastMonth');
        default: return '';
      }
    } else if (startDate && endDate) {
      return t('posReportTab.dateFilters.range', {
        start: startDate.toLocaleDateString(tr.code),
        end: endDate.toLocaleDateString(tr.code),
      });
    } else if (startDate) {
      return t('posReportTab.dateFilters.startOnly', { start: startDate.toLocaleDateString(tr.code) });
    } else if (endDate) {
      return t('posReportTab.dateFilters.endOnly', { end: endDate.toLocaleDateString(tr.code) });
    }
    return '';
  };
  
  const getPosNameFromTransaction = (transaction: BankTransaction): string => {
    const matchedPos = assignedPosInfos.find(pos => 
        pos.bankAccountId === transaction.bankAccountId && 
        (pos.subAccountId ? pos.subAccountId === transaction.bankSubAccountId : true)
    );
    return matchedPos ? matchedPos.name : (transaction.bankAccountName || t('posReportTab.common.unknownPos'));
  };

  // Benzersiz kasiyer isimlerini al
  const uniqueCashiers = useMemo(() => {
    if (posTransactions.length === 0) return [];
    const cashierSet = new Set(posTransactions.map((transaction) => transaction.createdBy || t('posReportTab.common.unknown')));
    return Array.from(cashierSet).sort();
  }, [posTransactions, t]);

  // Excel indirme fonksiyonu
  const exportToExcel = () => {
    if (filteredPosTransactions.length === 0) return;
    
    // Excel verilerini hazırla
    const excelData = filteredPosTransactions.map(transaction => {
      const typeInfo = getPosTransactionTypeInfo(transaction, t);
      const posName = getPosNameFromTransaction(transaction);
      
      return {
        [t('posReportTab.table.dateTime')]: formatDateTime(transaction.createdAt || transaction.date),
        [t('posReportTab.table.posOrBank')]: posName,
        [t('posReportTab.table.transactionType')]: typeInfo.label,
        [t('posReportTab.table.description')]: transaction.description || '',
        [t('posReportTab.table.amount')]: `${transaction.transactionType === 'QUICK_SALE' ? '+' : (transaction.amount < 0 ? '' : '+')}${formatCurrency(transaction.amount, transaction.currency)}`,
        [t('posReportTab.table.cashier')]: transaction.createdBy || ''
      };
    });

    // Excel çalışma kitabı oluştur
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 20 }, // Tarih/Saat
      { wch: 30 }, // POS Adı / Banka
      { wch: 15 }, // İşlem Tipi
      { wch: 40 }, // Açıklama
      { wch: 15 }, // Tutar
      { wch: 15 }  // Kasiyer
    ];
    ws['!cols'] = colWidths;

    // Çalışma sayfasını kitaba ekle
    utils.book_append_sheet(wb, ws, t('posReportTab.title'));

    // Dosya adını hazırla - Şu anki tarih ile
    const fileName = `POS_Report_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;

    // Excel dosyasını indir
    writeFile(wb, fileName);
  };

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
        {/* Başlık, Atanmış POS'lar, Toplam Tutar ve Filtreler Tek Satırda */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            {/* Sol Taraf: Başlık, POS Çipleri, Toplam Tutar */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mr: 1 }}>{t('posReportTab.title')}</Typography>
                {assignedPosInfos.length > 0 && 
                    assignedPosInfos.map((posInfo) => (
                        <Chip key={posInfo.id} label={`${posInfo.name} (${posInfo.bankDetails})`} color="secondary" variant="outlined" size="small" sx={{ mr:0.5, mb: 0.5 }} />
                    ))
                }
                {assignedPosInfos.length === 0 && (
                    <Chip label={t('posReportTab.emptyState.posAccountNotAssigned')} variant="outlined" size="small" sx={{ color: 'text.secondary', mr: 1, mb: 0.5 }} />
                )}
                {filteredPosTransactions.length > 0 && (
                    <Chip
                        label={`${t('posReportTab.summary.total')}: ${formatCurrency(totalFilteredPosAmount)}`}
                        color={totalFilteredPosAmount >= 0 ? "success" : "error"}
                        variant="filled"
                        size="small"
                        sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '4px 8px', mr: 1, mb: 0.5 }}
                    />
                )}
            </Box>

            {/* Sağ Taraf: Filtreler */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexGrow: 1 }}>
                {/* Kasiyer Filtresi */}
                {uniqueCashiers.length > 0 && (
                  <Autocomplete
                    options={uniqueCashiers}
                    value={selectedCashier}
                    onChange={(event, newValue) => {
                      setSelectedCashier(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('posReportTab.filters.selectCashier')}
                        size="small"
                        variant="outlined"
                        sx={{ width: { xs: '100%', sm: 180 }, mr: 1 }}
                      />
                    )}
                    sx={{ mb: { xs: 1, sm: 0 } }} /* Mobil için alt boşluk */
                  />
                )}
                {/* DateTimePicker Filtreleri - ✅ Saat ile */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1}}> 
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                        <DateTimePicker
                            label={t('posReportTab.filters.startDateTime')}
                            value={startDate}
                            onChange={(newValue) => { setStartDate(newValue); setDateFilter(null); }}
                            maxDateTime={endDate || undefined}
                            ampm={false} // 24 saat formatı
                            format="dd.MM.yyyy HH:mm" // Türkiye formatı
                            slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 200 } } } }}
                        />
                        <DateTimePicker
                            label={t('posReportTab.filters.endDateTime')}
                            value={endDate}
                            onChange={(newValue) => { setEndDate(newValue); setDateFilter(null); }}
                            minDateTime={startDate || undefined}
                            ampm={false} // 24 saat formatı
                            format="dd.MM.yyyy HH:mm" // Türkiye formatı
                            slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 200 } } } }}
                        />
                    </LocalizationProvider>
                    {(startDate || endDate || selectedCashier) && (
                        <Tooltip title={t('posReportTab.actions.clearAllFilters')}>
                            <IconButton onClick={() => { setStartDate(null); setEndDate(null); setDateFilter(null); setSelectedCashier(null); }} size="small">
                                <FilterAltOffIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                
                {/* Hazır Tarih Filtre Butonları */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {dateFilter && (
                        <Chip
                            icon={<FilterAltIcon />}
                            label={getDateFilterLabel()}
                            variant="filled"
                            color="primary"
                            size="small"
                            onDelete={() => setDateFilter(null)}
                            deleteIcon={<FilterAltOffIcon />}
                            sx={{ mr: 1.5 }}
                        />
                    )}
                    <ButtonGroup size="small" variant="outlined">
                        <Tooltip title={t('posReportTab.tooltips.todayRecords')}>
                            <Button onClick={() => { setDateFilter('today'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'today' ? 'primary' : 'inherit'} variant={dateFilter === 'today' ? 'contained' : 'outlined'} startIcon={<CalendarTodayIcon />}>{t('posReportTab.dateFilters.today')}</Button>
                        </Tooltip>
                        <Tooltip title={t('posReportTab.tooltips.last7DaysRecords')}>
                            <Button onClick={() => { setDateFilter('week'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'week' ? 'primary' : 'inherit'} variant={dateFilter === 'week' ? 'contained' : 'outlined'} startIcon={<DateRangeIcon />}>{t('posReportTab.dateFilters.lastWeek')}</Button>
                        </Tooltip>
                        <Tooltip title={t('posReportTab.tooltips.last30DaysRecords')}>
                            <Button onClick={() => { setDateFilter('month'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'month' ? 'primary' : 'inherit'} variant={dateFilter === 'month' ? 'contained' : 'outlined'} startIcon={<EventNoteIcon />}>{t('posReportTab.dateFilters.lastMonth')}</Button>
                        </Tooltip>
                    </ButtonGroup>
                    
                    {/* Excel İndirme Butonu */}
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<DownloadIcon />}
                      onClick={exportToExcel}
                      disabled={filteredPosTransactions.length === 0}
                      sx={{ ml: 2 }}
                    >
                      {t('posReportTab.actions.downloadExcel')}
                    </Button>
                </Box>
            </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>

      {assignedPosInfos.length === 0 ? (
        <Typography variant="body1" align="center" sx={{ py: 4 }}>
          {t('posReportTab.emptyState.noAssignedPosAccount')}
        </Typography>
      ) : loadingPosTransactions ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : posTransactionsError ? (
        <Alert severity="error" sx={{ mb: 2 }}>{posTransactionsError}</Alert>
      ) : filteredPosTransactions.length === 0 ? (
        <Typography variant="body1" align="center" sx={{ py: 4 }}>
          {dateFilter || startDate || endDate || selectedCashier
            ? t('posReportTab.emptyState.noRecordsForCriteria')
            : t('posReportTab.emptyState.noPosRecords')}
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('posReportTab.table.dateTime')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '20%' }}>{t('posReportTab.table.posOrBank')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('posReportTab.table.transactionType')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '25%' }}>{t('posReportTab.table.description')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%', textAlign: 'right' }}>{t('posReportTab.table.amount')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '10%' }}>{t('posReportTab.table.cashier')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPosTransactions.map((transaction) => {
                  const typeInfo = getPosTransactionTypeInfo(transaction, t);
                  const posName = getPosNameFromTransaction(transaction);
                  const amountColor = transaction.amount < 0 || typeInfo.color === 'error' ? 'error.main' : 'success.main';
                  
                  return (
                    <TableRow
                      key={transaction.id}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                         backgroundColor: typeInfo.color === 'success' ? 'rgba(237, 247, 237, 0.4)' : typeInfo.color === 'error' ? 'rgba(253, 237, 237, 0.4)' : undefined
                      }}
                    >
                      <TableCell sx={{ fontSize: '0.875rem' }}>{formatDateTime(transaction.createdAt || transaction.date)}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{posName}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        <Chip
                          icon={typeInfo.icon}
                          label={typeInfo.label}
                          size="small"
                          color={typeInfo.color}
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{transaction.description}</TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', fontWeight: 'medium', color: amountColor, textAlign: 'right' }}>
                        {transaction.transactionType === 'QUICK_SALE' ? '+' : (transaction.amount < 0 ? '' : '+')}
                        {formatCurrency(transaction.amount, transaction.currency)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>{transaction.createdBy || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ '& td': { fontWeight: 'bold', fontSize: '0.9rem' } }}>
                  <TableCell colSpan={4} sx={{ textAlign: 'right' }}>{t('posReportTab.summary.grandTotal')}</TableCell>
                  <TableCell sx={{ textAlign: 'right', color: overallPosTotalAmount >= 0 ? 'success.main' : 'error.main' }}>
                    {formatCurrency(overallPosTotalAmount)}
                  </TableCell>
                  <TableCell></TableCell> {/* Kasiyer sütunu için boş */}
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredPosTransactions.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('posReportTab.pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('posReportTab.pagination.displayedRows', { count, from, to })}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </>
      )}
    </Paper>
  );
};

export default PosReportTab; 